<template>
  <div class="d-flex flex-column align-items-center">

    <!-- TITLE -->
    <div
      v-if="currentQuestionReady"
      class="d-flex align-items-center justify-content-center position-relative w-100 px-12 text-center mt-5 mt-md-16 mb-4 mb-md-7 mb-lg-12 order-1"
      style="min-height: 48px;">
      <button
        v-if="currentOrder > 1"
        class="button button--iconed position-absolute top-0 left-0"
        :class="buttonStyle"
        @click="moveBack()">
        <svg-icon
          icon="arrow"
          size="md" />
      </button>
      <h1
        class="m-0 px-4 px-sm-8"
        :class="headingStyle">
        {{ currentQuestion.descr }}
      </h1>
    </div>

    <div
      v-if="locationShown && !environmentReady"
      class="d-flex align-items-center justify-content-center position-relative w-100 px-12 text-center mt-5 mt-md-16 mb-4 mb-md-7 mb-lg-12 order-1"
      style="min-height: 48px;">
      <button
        class="button button--iconed position-absolute top-0 left-0"
        :class="buttonStyle"
        :disabled="getEnvironmentResponse"
        @click="moveToLastQuestion()">
        <svg-icon
          icon="arrow"
          size="md" />
      </button>
      <h1
        class="m-0 px-4 px-sm-8"
        :class="headingStyle">
        Where would you be staying for the next 2 months?
      </h1>
    </div>

    <div
      v-if="!locationShown && environmentReady"
      class="d-flex align-items-center justify-content-center position-relative w-100 px-12 text-center mt-5 mt-md-16 mb-4 mb-md-7 mb-lg-12 order-1"
      style="min-height: 48px;">
      <button
        class="button button--iconed position-absolute top-0 left-0"
        :class="buttonStyle"
        @click="showLocation(), clearLocation()">
        <svg-icon
          icon="arrow"
          size="md" />
      </button>
      <h1
        class="m-0 px-4 px-sm-8"
        :class="headingStyle">
        Here's the environment your skin is exposed to <br>in {{ environmentLocation }}
      </h1>
    </div>
    <!-- // TITLE -->

    <!-- CONTENT -->
    <div
      v-if="currentQuestionReady"
      class="d-flex flex-wrap align-items-center mb-9 order-2"
      style="margin-left: -.5rem; margin-right: -.5rem"
      :class="[
        { 'justify-content-start justify-content-sm-center' : currentQuestion.layout !== 'image' },
        { 'justify-content-center' : currentQuestion.layout == 'image' },
      ]">
      <div
        v-for="answer in currentQuestion.answers.options"
        :key="answer.slug"
        class="p-2 w-sm-auto align-self-stretch"
        :class="[widthClass]">
        <div
          class="answer radius-small font-lg cursor-pointer no-select d-flex align-items-center h-100"
          :class="[
            { 'answer--selected shadow-type-1' : selectedAnswers.some(x => x == answer.id) },
            { 'py-4 px-4 px-sm-6 px-md-8 answer--bordered' : currentQuestion.layout !== 'image' },
            { 'p-2 answer--plane' : currentQuestion.layout == 'image' },
            { 'flex-row flex-sm-column' : currentQuestion.layout == 'text_box' },
            { 'flex-column justify-content-center' : currentQuestion.layout == 'icon_box' },
          ]"
          @click="chooseAnswer(answer)">
          <svg-icon
            v-if="currentQuestion.layout == 'icon_box' || currentQuestion.layout == 'image'"
            :icon="answer.slug"
            :size="iconSize"
            class="order-1"
            :class="{ 'mb-3' : currentQuestion.layout !== 'image' }" />
          <div
            v-if="currentQuestion.layout !== 'image' && !currentQuestion.answers.multiple"
            class="radio radius-circle flex-center position-relative"
            :class="[
              { 'order-2 mr-2 mr-sm-0 mb-0 mb-sm-2' : currentQuestion.layout == 'text_box' },
              { 'order-3 mt-2 mb-1' : currentQuestion.layout == 'icon_box'}
            ]" />
          <div
            v-if="currentQuestion.layout !== 'image' && currentQuestion.answers.multiple"
            class="checkbox radius-small flex-center position-relative"
            :class="[
              { 'order-2 mr-2 mr-sm-0 mb-0 mb-sm-2' : currentQuestion.layout == 'text_box' },
              { 'order-3 mt-2' : currentQuestion.layout == 'icon_box'}
            ]" />
          <span
            v-if="currentQuestion.layout !== 'image'"
            :class="[
              { 'order-3' : currentQuestion.layout == 'text_box' },
              { 'order-2 word-break-break-word text-center' : currentQuestion.layout == 'icon_box' },
            ]">
            {{ answer.name }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="locationShown && !environmentReady"
      v-click-outside-element="closeLocationDropdown"
      class="d-flex flex-wrap align-items-center mb-9 order-2 w-100"
      style="margin-left: -.5rem; margin-right: -.5rem">

      <div class="position-relative input w-100 mb-0">
        <div class="position-relative w-100 w-sm-50">
          <div id="scrollTarget" />
          <svg-icon
            icon="search"
            size="sm"
            ref="searchIcon"
            class="position-absolute top-0 bottom-0 left-4 m-auto" />
          <input
            id="locationInput"
            ref="locationInput"
            v-if="!selectedLocation.id"
            v-model="typedLocation"
            v-eager
            type="text"
            placeholder="Enter Your City and Country"
            class="input__field bg-gray-1 px-12 w-100"
            :class="{ 'input__field--focused' : typedLocation.length > 2 }"
            autocomplete="off"
            @click="openLocationDropdown">
          <input
            v-else
            :value="`${selectedLocation.name}, ${selectedLocation.country}`"
            type="text"
            class="input__field bg-gray-1 px-12 w-100"
            autocomplete="off"
            @click="restartSearch">
          <div
            ref="searchDropdown"
            v-if="typedLocation.length > 2 && locationDropdownOpened"
            class="dropdown">
            <VuePerfectScrollbar
              class="dropdown__scrollarea"
              :options="{
                wheelPropagation: true
              }">
              <div v-if="foundLocations.length >= 1 && typedLocation.length > 1">
                <div
                  v-for="location in foundLocations"
                  :key="`location-${location.id}`"
                  class="dropdown__item cursor-pointer px-4 no-select w-100 d-flex flex-column justify-content-center"
                  :class="location.id === selectedLocation.id ? 'dropdown__item--selected' : ''"
                  @click="selectLocation(location)">
                  <text-highlight :queries="searchQueries">
                    {{ location.name }}, {{ location.country }}
                  </text-highlight>
                </div>
              </div>
              <div
                v-if="foundLocations.length === 0 && typedLocation.length > 1 && !searchLocationResponse"
                class="flex-center flex-column text-center my-4">
                <svg-icon
                  icon="alert"
                  size="2xl"
                  class="mb-2" />
                <h4 class="mt-0 mb-3 clr-danger">There is no such location</h4>
                <p class="w-75 mb-0 clr-gray-4">Please check the spelling of the name or language settings were selected correctly </p>
              </div>
              <div
                v-if="searchLocationResponse"
                class="flex-center flex-column text-center my-4">
                <div class="dropdown__spinner mb-2" />
                <h4 class="mt-0 mb-3 clr-primary">
                  <span class="dropdown__loading">Looking for your location</span>
                </h4>
                <p class="w-75 mb-0 clr-gray-4">At the moment we are checking to get the appropriate result</p>
              </div>
            </VuePerfectScrollbar>
          </div>
        </div>
      </div>

    </div>

    <div
      v-if="!locationShown && environmentReady"
      class="d-flex flex-wrap align-items-center mb-9 order-2 w-100"
      style="margin-left: -.5rem; margin-right: -.5rem">
      <div class="environment d-flex flex-wrap align-items-center w-100 radius-small">
        <div
          v-for="(factor, key) in environment"
          :key="key"
          class="environment__item d-flex align-items-end w-100 w-sm-25 p-2">
          <svg-icon
            :icon="key"
            size="3xl" />
          <div class="d-flex flex-column justify-content-end flex-grow-1 pl-3">
            <div class="font-lg mb-1">
              <span>{{ key | beautifiedFactor }}</span>
              <span
                v-if="factor.units.length > 0"
                class="ml-1">
                ({{ factor.units }})
              </span>
            </div>
            <div
              class="d-flex align-items-center font-weight-600 py-1 px-2 radius-small"
              :class="factorColor(factor.cat)">
              <span>{{ factor.val }}</span>
              <span class="ml-auto text-capitalize">{{ factor.cat }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- // CONTENT -->

    <!-- TIPS -->
    <question-tip
      v-if="currentQuestionReady && currentQuestion.tip"
      :message="currentQuestion.tip" />

    <question-tip
      v-if="locationShown"
      :message="'Weather conditions is constantly affecting your skin statement, so further recommendations does consider this fact as one of the most important.'" />

    <hr
      v-if="(isDesktop && currentQuestionReady && currentQuestion.tip) || (isDesktop && locationShown)"
      class="my-7 order-4">
    <!-- // TIPS -->

    <!-- ACTION BUTTON -->
    <button
      v-if="currentQuestionReady"
      class="button button--main mb-7"
      :class="[
        { 'button--block' : currentBreakpoint == 'xs' },
        { 'order-5' : isDesktop },
        { 'order-4' : isMobile },
      ]"
      :disabled="selectedAnswers.length == 0"
      @click="moveForward()">
      Next Step
    </button>

    <button
      v-if="locationShown && !environmentReady"
      class="button button--main mb-7"
      :class="[
        { 'button--block' : currentBreakpoint == 'xs' },
        { 'order-5' : isDesktop },
        { 'order-4' : isMobile },
      ]"
      :disabled="!selectedLocation.id || getEnvironmentResponse"
      @click="getEnvironment()">
      <span
        v-if="getEnvironmentResponse"
        class="button__loading">Checking Enironment</span>
      <span v-else>Submit</span>
    </button>

    <button
      v-if="!locationShown && environmentReady"
      class="button button--main mb-3"
      :class="[
        { 'button--block' : currentBreakpoint == 'xs' },
        { 'order-5' : isDesktop },
        { 'order-4' : isMobile },
      ]"
      @click="getResults()">
      View Results
    </button>
    <!-- // ACTION BUTTON -->

  </div>
</template>

<script>
import QuestionTip from "@/components/QuestionTip"
import { eager } from '@kuroski/v-eager'

const initialLocation = {
  id: undefined,
  name: undefined,
  country: undefined,
}

export default {
  name: 'Questions',
  components: {
    QuestionTip,
  },
  directives: {
    eager,
  },
  filters: {
    beautifiedFactor(val) {
      if (val) {
        if (val == 'humidity') return 'Humidity'
        else if (val == 'polution') return 'Pollution'
        else if (val == 'temperature') return 'Temperature'
        else if (val == 'uv_index') return 'UV Index'
        else return '...'
      } else {
        return '...'
      }
    },
  },
  data() {
    return {
      selectedAnswers: [],

      typedLocation: '',
      selectedLocation: initialLocation,
      locationDropdownOpened: false,
    }
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    currentOrder(newVal, oldVal) {
      if (newVal > this.questionsLength) {
        this.showLocation()
      } else {
        this.hideLocation()
      }
    },

    /* eslint-disable-next-line no-unused-vars */
    typedLocation(newVal, oldVal) {
      if (newVal && newVal.length > 2) {
        this.searchLocation()
      }
    },
  },
  computed: {
    // CONFIG
    currentBreakpoint() {
      /// Returns current 'morphological' breakpoint value based on window width
      ///
      /// @example
      ///   'xs' | 'sm' | 'md' | 'lg' | 'xl'

      return this.$store.state.config.currentBreakpoint
    },

    isDesktop() {
      return this.currentBreakpoint == 'md' || this.currentBreakpoint == 'lg' || this.currentBreakpoint == 'xl'
    },

    isMobile() {
      return this.currentBreakpoint == 'xs' || this.currentBreakpoint == 'sm'
    },

    // CONDITIONAL STYLING
    widthClass() {
      if (this.currentQuestionReady) {
        const longNameLength = 8
        const answersArray = []
        const answerOptions = this.currentQuestion.answers.options
        const questionLayout = this.currentQuestion.layout

        for (let n = 0; n < answerOptions.length; n++) {
          answersArray.push(answerOptions[n].name)
        }

        const isLong = answersArray.some(i => i.length > longNameLength)

        if (questionLayout == 'image') {
          return 'w-auto'
        } else {
          if (questionLayout === 'icon_box') {
            return 'w-50'
          } else {
            return isLong ? 'w-100' : 'w-50'
          }
        }
      } else {
        return false
      }
    },

    iconSize() {
      if (this.currentQuestionReady) {
        const declaredSize = this.currentQuestion.icon_size

        if (this.currentQuestion.layout == 'image') {
          return '4xl'
        } else {
          return declaredSize == 'big' ? '4xl' : '2xl'
        }
      } else {
        return false
      }
    },

    buttonStyle() {
      if (this.isDesktop) return 'button--outline'
      else return 'button--text'
    },

    headingStyle() {
      if (this.isDesktop) return 'h1'
      else return 'h4'
    },

    // Q & A
    userUid() {
      return this.$store.state.user.uid
    },

    questions() {
      return this.$store.state.user.questions
    },

    questionsLength() {
      return this.questions.length
    },

    answers() {
      return this.$store.state.user.answers
    },

    answersLength() {
      return Object.keys(this.answers).length
    },

    currentOrder() {
      return this.$store.state.user.currentOrder
    },

    currentQuestion() {
      return this.questions.filter(i => i.order == this.currentOrder)[0]
    },

    currentQuestionReady() {
      if (this.currentQuestion) {
        return Object.keys(this.currentQuestion).length > 0
      } else {
        return false
      }
    },

    // LOCATION & EVIRONMENT
    locationShown() {
      return this.$store.state.user.locationShown
    },

    searchQueries() {
      return this.typedLocation.length > 1 ? this.typedLocation.split(' ') : []
    },

    foundLocations() {
      return this.$store.state.user.foundLocations
    },

    environment() {
      return this.$store.state.user.environment
    },

    environmentLocation() {
      return this.$store.state.user.environmentLocation
    },

    environmentReady() {
      return Object.keys(this.environment).length > 0
    },

    searchLocationResponse() {
      return this.$store.state.user.responses.searchLocation
    },

    getEnvironmentResponse() {
      return this.$store.state.user.responses.getEnvironment
    },
  },
  methods: {
    // Q & A
    showLastState() {
      if (this.currentOrder > this.questionsLength && !this.environmentReady) {
        this.showLocation()
      }
    },

    chooseAnswer(answer) {
      // If multi choose question
      if (this.currentQuestion.answers.multiple) {
        // If this answer excludes anothers, clear all made selections
        if (answer.exclude) {
          const others = this.selectedAnswers.filter(i => i !== answer.id)

          if (others.length > 0) {
            this.resetAnswer()
          }
        }
        // Otherwise if this excluded option is selected, remove it
        else {
          const options = this.currentQuestion.answers.options
          const excluded = options.filter(i => i.exclude)[0].id

          if (this.selectedAnswers.includes(excluded)) {
            this.selectedAnswers.splice(options.indexOf(excluded), 1)
          }
        }

        // If this answer isn't in answers yet, insert it
        if (!this.selectedAnswers.includes(answer.id)) {
          this.selectedAnswers.push(answer.id)
        }
        // Otherwise, remove it
        else {
          for (let i = this.selectedAnswers.length - 1; i >= 0; i--) {
            if (this.selectedAnswers[i] == answer.id) {
              this.selectedAnswers.splice(i, 1)
            }
          }
        }
      } else {
        this.selectedAnswers = [ answer.id ]
      }
    },

    resetAnswer() {
      this.selectedAnswers = []
    },

    preselectAnswer() {
      this.selectedAnswers = this.answers[this.currentOrder]
    },

    moveBack() {
      if (this.currentOrder == 1) {
        this.$store.commit('config/SET_CURRENT_TRANSITION', 'overlay-down-full')
        setTimeout(() => {
          this.$router.push({ name: 'Scanner' })
        }, 100)
      } else {
        this.$store.commit('user/SAVE_CURRENT_ORDER', this.currentQuestion.id - 1)

        this.preselectAnswer()
      }
    },

    moveForward() {
      if (this.selectedAnswers.length > 0) {
        window.scrollTo(0, 0)

        this.$store.dispatch('user/sendAnswer', {
          question: this.currentQuestion.id,
          answer: this.selectedAnswers
        })

        this.$store.commit('user/SAVE_CURRENT_ORDER', this.currentQuestion.id + 1)

        this.resetAnswer()
      }
    },

    // LOCATION & EVIRONMENT
    showLocation() {
      this.$store.commit('user/CHANGE_LOCATION_SHOW_STATUS', true)
      this.selectedLocation = initialLocation
      this.typedLocation = ''
      this.locationDropdownOpened = false
    },

    clearLocation() {
      this.$store.commit('user/SAVE_ENVIRONMENT', {})
      this.$store.commit('user/SAVE_ENVIRONMENT_LOCATION', {})
    },

    hideLocation() {
      this.$store.commit('user/CHANGE_LOCATION_SHOW_STATUS', false)
    },

    moveToLastQuestion() {
      this.$store.commit('user/CHANGE_LOCATION_SHOW_STATUS', false)
      this.$store.commit('user/SAVE_CURRENT_ORDER', this.answersLength)
      this.preselectAnswer()
    },

    searchLocation() {
      this.openLocationDropdown()
      this.$store.dispatch('user/searchLocation', this.typedLocation)
    },

    selectLocation(location) {
      this.selectedLocation = location
      this.typedLocation = ''
      this.closeLocationDropdown()
    },

    restartSearch() {
      this.typedLocation = `${this.selectedLocation.name}, ${this.selectedLocation.country}`
      this.selectedLocation = initialLocation
      this.openLocationDropdown()
    },

    openLocationDropdown() {
      this.$nextTick(() => {
        const scrollTarget = document.querySelector('#scrollTarget')
        scrollTarget.scrollIntoView({ behavior: "smooth" })
      })

      this.locationDropdownOpened = true
    },

    closeLocationDropdown() {
      this.locationDropdownOpened = false
    },

    getEnvironment() {
      this.$store.dispatch('user/getEnvironment', this.selectedLocation)
    },

    getResults() {
      this.$store.dispatch('user/getResults')
    },

    factorColor(cat) {
      if (cat) {
        if (cat == 'bad') return 'bg-danger-light-5 clr-danger-dark-2'
        else if (cat == 'normal') return 'bg-warning-light-5 clr-warning-dark-2'
        else if (cat == 'good') return 'bg-success-light-5 clr-success-dark-2'
      } else {
        return 'bg-gray-2 clr-dark'
      }
    },

    retakeQuiz() {

    },
  },
  mounted() {
    this.showLastState()
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/@core/globals';

.answer {
  min-width: 96px;
  color: color('gray-3');
  transition: transition(all);

  &--bordered {
    border-style: dashed;
    border-color: color('gray-3');
    border-width: 1px;

    &:hover {
      border-color: color('primary');
      color: color('primary');
    }
  }

  &--plane {
    border-style: dashed;
    border-color: transparent;
    border-width: 1px;

    &:hover {
      border-color: color('gray-3');
      color: color('primary');
    }
  }

  &--selected {
    background-color: color('white');
    border: 1px solid color('primary') !important;
    color: color('primary') !important;

    .radio,
    .checkbox {
      border: 1px solid color('primary') !important;

      &::before {
        opacity: 1;
        transform: scale(1);
      }
    }
  }
}

.radio {
  @include icon-size(20px);

  border: 1px solid color('gray-3');

  &::before {
    content: '';
    display: block;
    position: absolute;
    width: 12px;
    height: 12px;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    opacity: 0;
    transform: scale(0);
    border-radius: radius('circle');
    background-color: color('primary');
    transition: transition(all, base, emphasized);
  }
}

.checkbox {
  @include icon-size(20px);

  border: 1px solid color('gray-3');

  &::before {
    content: '';
    display: block;
    position: absolute;
    width: 16px;
    height: 16px;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    opacity: 0;
    transform: scale(0);
    border-radius: radius('small');
    background-color: color('primary');
    transition: transition(all, shorter, emphasized);
  }
}

.environment {
  border: 1px solid color('gray-3');

  &__item {
    &:not(:last-child) {
      border-right: none;
      border-bottom: 1px solid color('gray-2');

      @media #{$sm-up} {
        border-bottom: none;
        border-right: 1px solid color('gray-2');
      }
    }
  }
}
</style>
